import React from 'react';
import SentimentJourney from './SentimentJourney';

interface SentimentData {
  overall_sentiment: 'positive' | 'negative' | 'neutral';
  confidence_score: number;
  positive_score: number;
  negative_score: number;
  neutral_score: number;
}

interface EmotionData {
  emotion_type: string;
  intensity: number;
  confidence: number;
  text_evidence?: string;
}

interface SentimentDisplayProps {
  sentiment: SentimentData | null;
  emotions: EmotionData[];
  size?: 'sm' | 'md' | 'lg';
  showDetails?: boolean;
  transcriptId?: string;
}

const SentimentDisplay: React.FC<SentimentDisplayProps> = ({ 
  sentiment, 
  emotions, 
  size = 'md',
  showDetails = false,
  transcriptId
}) => {
  if (!sentiment) {
    return null;
  }

  const getSentimentColor = (sentimentType: string) => {
    switch (sentimentType) {
      case 'positive':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'negative':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'neutral':
        return 'text-gray-600 bg-gray-50 border-gray-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getEmotionColor = (emotionType: string) => {
    const emotionColors: Record<string, string> = {
      joy: 'text-yellow-600 bg-yellow-50 border-yellow-200',
      happiness: 'text-yellow-600 bg-yellow-50 border-yellow-200',
      excitement: 'text-orange-600 bg-orange-50 border-orange-200',
      enthusiasm: 'text-orange-600 bg-orange-50 border-orange-200',
      contentment: 'text-green-600 bg-green-50 border-green-200',
      relief: 'text-green-600 bg-green-50 border-green-200',
      sadness: 'text-blue-600 bg-blue-50 border-blue-200',
      disappointment: 'text-blue-600 bg-blue-50 border-blue-200',
      anger: 'text-red-600 bg-red-50 border-red-200',
      frustration: 'text-red-600 bg-red-50 border-red-200',
      fear: 'text-purple-600 bg-purple-50 border-purple-200',
      anxiety: 'text-purple-600 bg-purple-50 border-purple-200',
      worry: 'text-purple-600 bg-purple-50 border-purple-200',
      surprise: 'text-pink-600 bg-pink-50 border-pink-200',
      disgust: 'text-gray-600 bg-gray-50 border-gray-200',
    };
    return emotionColors[emotionType] || 'text-gray-600 bg-gray-50 border-gray-200';
  };

  const getSentimentIcon = (sentimentType: string) => {
    switch (sentimentType) {
      case 'positive':
        return '😊';
      case 'negative':
        return '😔';
      case 'neutral':
        return '😐';
      default:
        return '😐';
    }
  };

  const getEmotionIcon = (emotionType: string) => {
    const emotionIcons: Record<string, string> = {
      joy: '😄',
      happiness: '😊',
      excitement: '🤩',
      enthusiasm: '🎉',
      contentment: '😌',
      relief: '😅',
      sadness: '😢',
      disappointment: '😞',
      anger: '😠',
      frustration: '😤',
      fear: '😨',
      anxiety: '😰',
      worry: '😟',
      surprise: '😲',
      disgust: '🤢',
    };
    return emotionIcons[emotionType] || '😐';
  };

  const sizeClasses = {
    sm: 'text-xs px-2 py-1',
    md: 'text-sm px-3 py-1.5',
    lg: 'text-base px-4 py-2'
  };

  const formatPercentage = (score: number) => {
    return `${Math.round(score * 100)}%`;
  };

  return (
    <div className="sentiment-display">
      {/* Overall Sentiment */}
      <div className="flex items-center gap-2 mb-2">
        <span 
          className={`inline-flex items-center gap-1 rounded-full border font-medium ${getSentimentColor(sentiment.overall_sentiment)} ${sizeClasses[size]}`}
          title={`Confidence: ${formatPercentage(sentiment.confidence_score)}`}
        >
          <span>{getSentimentIcon(sentiment.overall_sentiment)}</span>
          <span className="capitalize">{sentiment.overall_sentiment}</span>
          <span className="text-xs opacity-75">
            {formatPercentage(sentiment.confidence_score)}
          </span>
        </span>
      </div>

      {/* Detailed Scores */}
      {showDetails && (
        <div className="mb-3">
          <div className="text-xs text-gray-600 mb-1">Sentiment Breakdown:</div>
          <div className="flex gap-2 text-xs">
            <span className="text-green-600">
              Positive: {formatPercentage(sentiment.positive_score)}
            </span>
            <span className="text-red-600">
              Negative: {formatPercentage(sentiment.negative_score)}
            </span>
            <span className="text-gray-600">
              Neutral: {formatPercentage(sentiment.neutral_score)}
            </span>
          </div>
        </div>
      )}

      {/* Emotions */}
      {emotions.length > 0 && (
        <div>
          <div className="text-xs text-gray-600 mb-1">Emotions Detected:</div>
          <div className="flex flex-wrap gap-1">
            {emotions.slice(0, showDetails ? emotions.length : 3).map((emotion, index) => (
              <span
                key={index}
                className={`inline-flex items-center gap-1 rounded-full border text-xs px-2 py-1 ${getEmotionColor(emotion.emotion_type)}`}
                title={`Intensity: ${formatPercentage(emotion.intensity)}, Confidence: ${formatPercentage(emotion.confidence)}${emotion.text_evidence ? `\nEvidence: "${emotion.text_evidence}"` : ''}`}
              >
                <span>{getEmotionIcon(emotion.emotion_type)}</span>
                <span className="capitalize">{emotion.emotion_type}</span>
                <span className="opacity-75">
                  {formatPercentage(emotion.intensity)}
                </span>
              </span>
            ))}
            {!showDetails && emotions.length > 3 && (
              <span className="text-xs text-gray-500 px-2 py-1">
                +{emotions.length - 3} more
              </span>
            )}
          </div>
        </div>
      )}

      {showDetails && transcriptId && (
        <SentimentJourney transcriptId={transcriptId} />
      )}
    </div>
  );
};

export default SentimentDisplay; 